import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { RefreshCw, ChevronRight, Clock } from "lucide-react";
import { toast } from "sonner";
import { relativeMinutes } from "@/lib/format";

const COLUMNS = [
    { key: "received", label: "New", color: "hsl(var(--status-new))", nextStatus: "preparing", nextLabel: "Start preparing" },
    { key: "preparing", label: "Preparing", color: "hsl(var(--status-preparing))", nextStatus: "ready", nextLabel: "Mark ready" },
    { key: "ready", label: "Ready", color: "hsl(var(--status-ready))", nextStatus: "served", nextLabel: "Mark served" },
    { key: "served", label: "Served", color: "hsl(var(--status-served))", nextStatus: null, nextLabel: null },
];

export default function KitchenScreen({ restaurantId }) {
    const [orders, setOrders] = useState([]);
    const [refreshing, setRefreshing] = useState(false);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setRefreshing(true);
        try {
            const url = restaurantId
                ? `/kitchen/orders/active?restaurant_id=${restaurantId}`
                : "/kitchen/orders/active";
            const { data } = await api.get(url);
            setOrders(data || []);
        } catch (error) {
            console.warn("[kitchen] load failed", error?.message);
        } finally {
            setRefreshing(false);
            setLoading(false);
        }
    }, [restaurantId]);

    useEffect(() => {
        load();
        const id = setInterval(load, 4000);
        return () => clearInterval(id);
    }, [load]);

    const itemsByStatus = useMemo(() => bucketByStatus(orders), [orders]);

    const advance = async (item) => {
        const next = COLUMNS.find((c) => c.key === item.status)?.nextStatus;
        if (!next) return;
        try {
            await api.patch(`/orders/${item.order_id}/items/${item.id}/status`, { status: next });
            toast.success(`→ ${next}`);
            load();
        } catch (error) {
            console.warn("[kitchen] status update failed", error?.message);
            toast.error("Could not update status");
        }
    };

    if (loading) return <Skeleton className="h-72 w-full" />;

    return (
        <div>
            <div className="flex items-center justify-end mb-3">
                <div className="text-xs text-muted-foreground inline-flex items-center gap-1" data-testid="kitchen-screen-status">
                    <RefreshCw className={`h-3 w-3 ${refreshing ? "animate-spin" : "opacity-30"}`} /> live · auto-refresh
                </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kitchen-kanban-board">
                {COLUMNS.map((col) => (
                    <KanbanColumn key={col.key} column={col} items={itemsByStatus[col.key] || []} onAdvance={advance} />
                ))}
            </div>
        </div>
    );
}

function KanbanColumn({ column, items, onAdvance }) {
    return (
        <div className="min-w-0">
            <div className="flex items-center justify-between mb-2">
                <div className="inline-flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: column.color }} />
                    <span className="font-medium">{column.label}</span>
                </div>
                <Badge variant="outline">{items.length}</Badge>
            </div>
            <ScrollArea className="h-[70vh] pr-2">
                <div className="space-y-2">
                    {items.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-6 text-center">No tickets</div>
                    ) : (
                        items.map((item) => (
                            <KitchenTicket key={item.id} item={item} column={column} onAdvance={onAdvance} />
                        ))
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}

function KitchenTicket({ item, column, onAdvance }) {
    const ageMin = (Date.now() - new Date(item.created_at).getTime()) / 60000;
    const urgent = item.status !== "served" && ageMin > 12;
    return (
        <Card
            className={urgent ? "border-l-4 border-l-[hsl(var(--destructive))] bg-[hsl(var(--destructive)/0.06)]" : ""}
            data-testid="kitchen-order-ticket"
        >
            <CardContent className="p-3">
                <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Table {item.table_label}</div>
                    <div className="text-xs text-muted-foreground inline-flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {relativeMinutes(item.created_at)}
                    </div>
                </div>
                <div className="font-medium mt-1">
                    {item.quantity}× {item.name}
                </div>
                {item.notes && <div className="text-xs italic text-muted-foreground mt-1">“{item.notes}”</div>}
                <div className="text-xs text-muted-foreground mt-1">{item.customer_name}</div>
                {column.nextStatus && (
                    <Button
                        size="sm"
                        className="w-full mt-2"
                        onClick={() => onAdvance(item)}
                        data-testid="kitchen-ticket-advance-button"
                    >
                        {column.nextLabel} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                    </Button>
                )}
            </CardContent>
        </Card>
    );
}

function bucketByStatus(orders) {
    const map = { received: [], preparing: [], ready: [], served: [] };
    for (const order of orders) {
        for (const item of order.items) {
            const bucket = map[item.status];
            if (!bucket) continue;
            bucket.push({
                ...item,
                order_id: order.id,
                table_label: order.table?.label || "—",
                customer_name: order.customer?.name || "Guest",
            });
        }
    }
    return map;
}
